import type { CategoryNodeMap } from '../category/category-node-map';
import type { EAN } from '../shared/ean';
import { toMasked } from '../shared/masked-ean';
import { likeLiteral, postgrestQuote } from '../shared/postgrest-filter';
import type { Condition, SkuPredicate } from './sku-predicate';

/**
 * The result of compiling a SkuPredicate against the PostgREST query surface.
 *
 * match-all  — spec = true; no filter applied (full-table scan in evaluator).
 * short-circuit — spec = false; evaluator skips the query entirely (empty set).
 * filter     — caller applies .or(orString) to the Supabase builder, then paginates.
 */
export type CompiledSpec =
  | { readonly kind: 'match-all' }
  | { readonly kind: 'short-circuit' }
  | { readonly kind: 'filter'; readonly orString: string };

/**
 * Compile a Segment predicate into a PostgREST query description.
 *
 * Throws before any network I/O when the predicate contains a condition
 * this compiler cannot express (e.g. category-under with a nodeId absent
 * from the caller-supplied node map). No silent narrowing is possible:
 * a throw here terminates evaluation, never falls through to a partial query.
 *
 * nodeMap is a port — callers supply it; this function never loads portal_categories.
 */
export function compileSpec(spec: SkuPredicate, nodeMap: CategoryNodeMap): CompiledSpec {
  const result = compileToString(spec, nodeMap);
  if (result === 'match-all') return { kind: 'match-all' };
  if (result === 'short-circuit') return { kind: 'short-circuit' };
  return { kind: 'filter', orString: result };
}

type Sentinel = 'match-all' | 'short-circuit';

function compileToString(spec: SkuPredicate, nodeMap: CategoryNodeMap): string | Sentinel {
  if (spec === true) return 'match-all';
  if (spec === false) return 'short-circuit';

  switch (spec.kind) {
    case 'all':
      return compileAll(spec.predicates, nodeMap);
    case 'any':
      return compileAny(spec.predicates, nodeMap);
    case 'not':
      return compileNot(spec.predicate, nodeMap);
    default:
      return compileCondition(spec, nodeMap);
  }
}

function compileCondition(cond: Condition, nodeMap: CategoryNodeMap): string {
  switch (cond.kind) {
    case 'brand-in': {
      if (cond.slugs.length === 1) return `brand_slug.eq.${postgrestQuote(cond.slugs[0])}`;
      return `brand_slug.in.(${cond.slugs.map(postgrestQuote).join(',')})`;
    }
    case 'ean-in':
      return `EAN.in.(${eanWireList(cond.eans)})`;
    case 'category-under': {
      const accessKey = nodeMap.idToAccessKey(cond.nodeId);
      if (accessKey === undefined) {
        throw new Error(
          `SegmentCompiler: cannot express category-under "${cond.nodeId}" — no access key in the supplied node map. ` +
            `Ensure portal_categories rows are loaded before compiling.`
        );
      }
      const prefix = postgrestQuote(`${likeLiteral(accessKey)}%`);
      return `or(category.like.${prefix},altcategory.like.${prefix})`;
    }
    case 'category-is':
      // Equality, on the category column alone. The value is a catalog category as the
      // catalog spells it, so no pattern is involved: `_` and `%` in it are literal, and
      // altcategory -- a second filing of a product -- is deliberately not consulted.
      return `category.eq.${postgrestQuote(cond.value)}`;
    case 'tag-has-any': {
      const parts = cond.tags.map(tagIlikeExpr);
      if (parts.length === 1) return parts[0];
      return `or(${parts.join(',')})`;
    }
    case 'tag-has-all': {
      const parts = cond.tags.map(tagIlikeExpr);
      if (parts.length === 1) return parts[0];
      return `and(${parts.join(',')})`;
    }
    case 'pclass-in': {
      if (cond.pclasses.length === 1) return `pclass.eq.${postgrestQuote(cond.pclasses[0])}`;
      return `pclass.in.(${cond.pclasses.map(postgrestQuote).join(',')})`;
    }
    case 'pclass-not-in': {
      if (cond.pclasses.length === 1) return `pclass.neq.${postgrestQuote(cond.pclasses[0])}`;
      return `pclass.not.in.(${cond.pclasses.map(postgrestQuote).join(',')})`;
    }
    case 'price-gte':
      return `price_1.gte.${cond.value}`;
    case 'price-lte':
      return `price_1.lte.${cond.value}`;
    case 'price-between':
      return `and(price_1.gte.${cond.lo},price_1.lte.${cond.hi})`;
    default: {
      const _exhaustive: never = cond;
      throw new Error(
        `SegmentCompiler: unhandled condition kind "${(_exhaustive as Condition).kind}"`
      );
    }
  }
}

/**
 * The quoted in-list an EAN condition compiles to: the one key form the catalog stores
 * each code under.
 *
 * The domain vocabulary is the real EAN and the catalog's is the masked key (prod survey
 * 2026-08-31), so the filter asks for the masked form and nothing else. Encoding here is
 * what keeps the wire vocabulary inside this ACL.
 */
function eanWireList(eans: readonly EAN[]): string {
  const forms = new Set<string>(eans.map(toMasked));
  return [...forms].map(postgrestQuote).join(',');
}

function tagIlikeExpr(tag: string): string {
  // B2C quoting: tags column stores a JSON array; match the JSON-quoted value within it.
  // postgrestQuote wraps the entire pattern so , ( ) " in tag strings cannot break out;
  // likeLiteral keeps a tag's own _ and % from matching as wildcards.
  return `tags.ilike.${postgrestQuote(`%"${likeLiteral(tag)}"%`)}`;
}

// PostgREST logic-tree grammar negates a leaf as field.not.op, never not.field (PGRST100).
function negatedTagIlikeExpr(tag: string): string {
  return `tags.not.ilike.${postgrestQuote(`%"${likeLiteral(tag)}"%`)}`;
}

function compileAll(
  predicates: readonly SkuPredicate[],
  nodeMap: CategoryNodeMap
): string | Sentinel {
  const parts: string[] = [];
  for (const p of predicates) {
    const result = compileToString(p, nodeMap);
    // Short-circuit is absorbing for AND (false AND anything = false).
    if (result === 'short-circuit') return 'short-circuit';
    // match-all is the identity for AND (true AND x = x); skip it.
    if (result === 'match-all') continue;
    parts.push(result);
  }
  if (parts.length === 0) return 'match-all';
  if (parts.length === 1) return parts[0];
  return `and(${parts.join(',')})`;
}

function compileAny(
  predicates: readonly SkuPredicate[],
  nodeMap: CategoryNodeMap
): string | Sentinel {
  const parts: string[] = [];
  for (const p of predicates) {
    const result = compileToString(p, nodeMap);
    // match-all is absorbing for OR (true OR anything = true).
    if (result === 'match-all') return 'match-all';
    // short-circuit is the identity for OR (false OR x = x); skip it.
    if (result === 'short-circuit') continue;
    parts.push(result);
  }
  if (parts.length === 0) return 'short-circuit';
  if (parts.length === 1) return parts[0];
  return `or(${parts.join(',')})`;
}

function compileNot(predicate: SkuPredicate, nodeMap: CategoryNodeMap): string | Sentinel {
  if (predicate === true) return 'short-circuit'; // NOT true = false
  if (predicate === false) return 'match-all'; // NOT false = true

  switch (predicate.kind) {
    case 'not':
      // Double negation elimination: NOT(NOT x) = x
      return compileToString(predicate.predicate, nodeMap);
    case 'all':
      // De Morgan: NOT(AND(c1,c2,...)) = OR(NOT c1, NOT c2, ...)
      return compileAny(
        predicate.predicates.map((p) => ({ kind: 'not' as const, predicate: p })),
        nodeMap
      );
    case 'any':
      // De Morgan: NOT(OR(c1,c2,...)) = AND(NOT c1, NOT c2, ...)
      return compileAll(
        predicate.predicates.map((p) => ({ kind: 'not' as const, predicate: p })),
        nodeMap
      );
    default:
      return negateLeafCondition(predicate, nodeMap);
  }
}

function negateLeafCondition(cond: Condition, nodeMap: CategoryNodeMap): string {
  switch (cond.kind) {
    case 'brand-in': {
      if (cond.slugs.length === 1) return `brand_slug.not.eq.${postgrestQuote(cond.slugs[0])}`;
      return `brand_slug.not.in.(${cond.slugs.map(postgrestQuote).join(',')})`;
    }
    case 'ean-in':
      // The exclusion mirrors the inclusion: the key form the catalog stores.
      return `EAN.not.in.(${eanWireList(cond.eans)})`;
    case 'category-under': {
      const accessKey = nodeMap.idToAccessKey(cond.nodeId);
      if (accessKey === undefined) {
        throw new Error(
          `SegmentCompiler: cannot express NOT category-under "${cond.nodeId}" — no access key in the supplied node map.`
        );
      }
      // NOT(A OR B) = AND(NOT A, NOT B)
      const prefix = postgrestQuote(`${likeLiteral(accessKey)}%`);
      return `and(category.not.like.${prefix},altcategory.not.like.${prefix})`;
    }
    case 'category-is':
      // The exclusion mirrors the inclusion: the one column, the one value.
      return `category.not.eq.${postgrestQuote(cond.value)}`;
    case 'tag-has-any': {
      // NOT(OR(ilike t1, ilike t2, ...)) = AND(NOT ilike t1, NOT ilike t2, ...)
      const parts = cond.tags.map((t) => negatedTagIlikeExpr(t));
      if (parts.length === 1) return parts[0];
      return `and(${parts.join(',')})`;
    }
    case 'tag-has-all': {
      // NOT(AND(ilike t1, ilike t2, ...)) = OR(NOT ilike t1, NOT ilike t2, ...)
      const parts = cond.tags.map((t) => negatedTagIlikeExpr(t));
      if (parts.length === 1) return parts[0];
      return `or(${parts.join(',')})`;
    }
    case 'pclass-in': {
      if (cond.pclasses.length === 1) return `pclass.not.eq.${postgrestQuote(cond.pclasses[0])}`;
      return `pclass.not.in.(${cond.pclasses.map(postgrestQuote).join(',')})`;
    }
    case 'pclass-not-in': {
      // NOT(pclass NOT IN (...)) = pclass IN (...)
      if (cond.pclasses.length === 1) return `pclass.eq.${postgrestQuote(cond.pclasses[0])}`;
      return `pclass.in.(${cond.pclasses.map(postgrestQuote).join(',')})`;
    }
    case 'price-gte':
      return `price_1.lt.${cond.value}`;
    case 'price-lte':
      return `price_1.gt.${cond.value}`;
    case 'price-between':
      // NOT(lo <= price <= hi) = price < lo OR price > hi
      return `or(price_1.lt.${cond.lo},price_1.gt.${cond.hi})`;
    default: {
      const _exhaustive: never = cond;
      throw new Error(
        `SegmentCompiler: unhandled condition kind in NOT "${(_exhaustive as Condition).kind}"`
      );
    }
  }
}
